import React, { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import Modal from '../../../../components/Modal';
import TextField from '../../../../components/TextField';
import Button from '../../../../components/Button';
import { FiEdit2, FiX, FiMail, FiPhone, FiHash, FiMapPin } from 'react-icons/fi';
import Icon from '../../../../components/Icon';
import type { Company } from '../../service/companiesService';

type CompanyDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    company: Company | null;
    bankId?: string;
    onUpdate?: () => void;
};

export default function CompanyDetailsModal({ isOpen, onClose, company, bankId, onUpdate }: CompanyDetailsModalProps) {
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editedCompany, setEditedCompany] = useState<Partial<Company>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
            setEditedCompany({});
            setErrors({});
        } else if (company) {
            setEditedCompany({
                companyName: company.companyName || '',
                companyType: company.companyType || '',
                companyId: company.companyId || '',
                companyNo: company.companyNo || '',
                email: company.email || '',
                companyAddress: company.companyAddress || '',
                contactPersonName: company.contactPersonName || '',
                contactMobileNumber: company.contactMobileNumber || '',
                contactEmail: company.contactEmail || '',
                status: company.status || 'Active',
            });
        }
    }, [isOpen, company]);

    const handleInputChange = (field: string, value: string) => {
        setEditedCompany(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!editedCompany.companyName?.trim() || !editedCompany.companyType) {
            newErrors.submit = 'Please fill in company name and type.';
            return false;
        }

        // Additional validation for Guard Company
        if (editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV') {
            if (
                !editedCompany.companyId?.trim() ||
                !editedCompany.companyNo?.trim() ||
                !editedCompany.email?.trim() ||
                !editedCompany.companyAddress?.trim() ||
                !editedCompany.contactPersonName?.trim() ||
                !editedCompany.contactMobileNumber?.trim() ||
                !editedCompany.contactEmail?.trim()
            ) {
                newErrors.submit =
                    'Please fill in all required fields: Company ID, Phone Number, Email, Address, Contact Person Name, Contact Mobile Number, and Contact Email.';
                return false;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!company || !bankId) return;

        if (!validateForm()) return;

        setLoading(true);
        try {
            const companyRef = doc(db, 'banks', bankId, 'companies', company.id);
            await updateDoc(companyRef, {
                companyName: editedCompany.companyName?.trim(),
                companyType: editedCompany.companyType,
                companyId: editedCompany.companyId?.trim() || null,
                companyNo: editedCompany.companyNo?.trim() || null,
                email: editedCompany.email?.trim() || null,
                companyAddress: editedCompany.companyAddress?.trim() || null,
                contactPersonName: editedCompany.contactPersonName?.trim() || null,
                contactMobileNumber: editedCompany.contactMobileNumber?.trim() || null,
                contactEmail: editedCompany.contactEmail?.trim() || null,
                status: editedCompany.status || 'Active',
                updatedAt: new Date(),
            });

            setIsEditing(false);
            if (onUpdate) {
                onUpdate();
            }
        } catch (err: any) {
            console.error('Error updating company:', err);
            setErrors({ submit: err.message || 'Failed to update company' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (company) {
            setEditedCompany({
                companyName: company.companyName || '',
                companyType: company.companyType || '',
                companyId: company.companyId || '',
                companyNo: company.companyNo || '',
                email: company.email || '',
                companyAddress: company.companyAddress || '',
                contactPersonName: company.contactPersonName || '',
                contactMobileNumber: company.contactMobileNumber || '',
                contactEmail: company.contactEmail || '',
                status: company.status || 'Active',
            });
        }
        setErrors({});
        setIsEditing(false);
    };

    const handleClose = () => {
        if (isEditing) {
            handleCancel();
        }
        onClose();
    };

    if (!company) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Company Details"
            size="lg"
        >
            <div className="p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Edit Button - Only show for bank users */}
                {isBankUser && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            aria-label="Edit company details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Company Name *"
                            value={editedCompany.companyName ?? company.companyName ?? ''}
                            onChange={(e) => handleInputChange('companyName', e.target.value)}
                            readOnly={!isBankUser || (!isEditing || company.isSelectedCompany)}
                            prefix={<Icon icon={FiHash} className="text-gray-400" />}
                            className={errors.companyName ? 'border-red-500' : ''}
                        />
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Company Type *
                            </label>
                            <input
                                type="text"
                                value={editedCompany.companyType ?? company.companyType ?? ''}
                                readOnly
                                className="w-full rounded-xl border border-gray-300 bg-gray-100 py-2.5 px-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Company ID *"
                            value={editedCompany.companyId ?? company.companyId ?? ''}
                            onChange={(e) => handleInputChange('companyId', e.target.value)}
                            placeholder="Enter company ID (e.g., MG-001)"
                            readOnly={!isBankUser || (!isEditing || company.isSelectedCompany)}
                            required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                        />
                        <TextField
                            label="Phone Number *"
                            type="tel"
                            value={editedCompany.companyNo ?? company.companyNo ?? ''}
                            onChange={(e) => handleInputChange('companyNo', e.target.value)}
                            placeholder="Enter phone number (e.g., +92-51-274-6011)"
                            readOnly={!isBankUser || (!isEditing || company.isSelectedCompany)}
                            required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                        />
                    </div>

                    <TextField
                        label="Email *"
                        type="email"
                        value={editedCompany.email ?? company.email ?? ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter email address (e.g., info@metroguardspk.com)"
                        readOnly={!isBankUser || (!isEditing || company.isSelectedCompany)}
                        required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                    />

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Company Address *
                        </label>
                        <textarea
                            value={editedCompany.companyAddress ?? company.companyAddress ?? ''}
                            onChange={(e) => handleInputChange('companyAddress', e.target.value)}
                            placeholder="Enter company address"
                            readOnly={!isBankUser || (!isEditing || company.isSelectedCompany)}
                            rows={3}
                            required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                            className="w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextField
                            label="Contact Person Name *"
                            value={editedCompany.contactPersonName ?? company.contactPersonName ?? ''}
                            onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                            placeholder="Enter contact person name"
                            readOnly={!isBankUser || !isEditing}
                            required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                        />
                        <TextField
                            label="Contact Mobile Number *"
                            type="tel"
                            value={editedCompany.contactMobileNumber ?? company.contactMobileNumber ?? ''}
                            onChange={(e) => handleInputChange('contactMobileNumber', e.target.value)}
                            placeholder="Enter contact mobile number (e.g., +92-300-1234567)"
                            readOnly={!isBankUser || !isEditing}
                            required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                        />
                    </div>

                    <TextField
                        label="Contact Email *"
                        type="email"
                        value={editedCompany.contactEmail ?? company.contactEmail ?? ''}
                        onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                        placeholder="Enter contact email address"
                        readOnly={!isBankUser || !isEditing}
                        required={editedCompany.companyType === 'Guard Company' || editedCompany.companyType === 'CCTV'}
                    />

                    {isEditing && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                            <select
                                value={editedCompany.status ?? company.status ?? 'Active'}
                                onChange={(e) => handleInputChange('status', e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                                <option value="Suspended">Suspended</option>
                            </select>
                        </div>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}

